/** setTimeout overflows above ~24.8 days; anything later is re-armed by a future sync. */
const MAX_TIMER_MS = 2 ** 31 - 1;
/** Shown ids remembered (shared by every tab through storage) so a reminder shows once. */
const FIRED_LIMIT = 200;

export interface SchedulerItem {
  id: string;
  taskId: string;
  title: string;
  body: string;
  fireAt: string;
}

export interface SchedulerDeps<T extends SchedulerItem> {
  /** Future schedule for this device (the API omits items whose time has passed). */
  fetch(): Promise<T[]>;
  ack(id: string): Promise<unknown>;
  present(item: T): void;
  allowed(): boolean;
  loadFired(): string[];
  saveFired(ids: string[]): void;
  now?: () => number;
}

/**
 * Arms one timer per scheduled notification and reconciles on every sync: items the server stops
 * returning are cancelled, unless their time already came (the server drops past items, so a timer
 * that a sleeping tab hasn't run yet fires now instead of being lost). Shown ids are deduplicated
 * across syncs, reloads, and tabs, and acknowledged after presentation.
 */
export class NotificationTimers<T extends SchedulerItem> {
  private armed = new Map<string, { item: T; timer: ReturnType<typeof setTimeout> | null }>();
  private syncing: Promise<void> | null = null;
  private again = false;
  private stopped = false;

  constructor(private readonly deps: SchedulerDeps<T>) {}

  private now(): number {
    return this.deps.now?.() ?? Date.now();
  }

  /** Ids currently waiting for their time (tests and diagnostics). */
  get pending(): string[] {
    return [...this.armed.keys()];
  }

  /** Fetch and reconcile. Concurrent calls coalesce into one follow-up sync. */
  sync(): Promise<void> {
    if (this.syncing) {
      this.again = true;
      return this.syncing;
    }
    this.syncing = (async () => {
      try {
        do {
          this.again = false;
          await this.syncOnce();
        } while (this.again && !this.stopped);
      } finally {
        this.syncing = null;
      }
    })();
    return this.syncing;
  }

  private async syncOnce(): Promise<void> {
    if (this.stopped) return;
    if (!this.deps.allowed()) {
      this.clear();
      return;
    }
    let items: T[];
    try {
      items = await this.deps.fetch();
    } catch {
      return; // offline: keep what is armed and retry on the next sync
    }
    if (this.stopped) return;
    const now = this.now();
    const current = new Map(items.map((i) => [i.id, i]));
    for (const [id, entry] of this.armed) {
      if (current.has(id)) continue;
      if (Date.parse(entry.item.fireAt) <= now) this.fire(entry.item);
      else this.cancel(id);
    }
    const fired = new Set(this.deps.loadFired());
    for (const item of items) {
      if (fired.has(item.id) || this.armed.has(item.id)) continue;
      this.arm(item, now);
    }
  }

  /** Fire anything whose time has passed (a tab waking from sleep may run timers late or not yet). */
  fireDue(): void {
    const now = this.now();
    for (const { item } of [...this.armed.values()]) if (Date.parse(item.fireAt) <= now) this.fire(item);
  }

  private arm(item: T, now: number) {
    const delay = Date.parse(item.fireAt) - now;
    if (delay > MAX_TIMER_MS) return;
    const timer = setTimeout(() => this.fire(item), Math.max(0, delay));
    this.armed.set(item.id, { item, timer });
  }

  private cancel(id: string) {
    const entry = this.armed.get(id);
    if (entry?.timer) clearTimeout(entry.timer);
    this.armed.delete(id);
  }

  private fire(item: T) {
    this.cancel(item.id);
    if (this.stopped || !this.deps.allowed()) return;
    // Re-read storage so another tab that already showed it wins.
    const fired = this.deps.loadFired();
    if (fired.includes(item.id)) return;
    this.deps.saveFired([...fired, item.id].slice(-FIRED_LIMIT));
    try {
      this.deps.present(item);
    } catch {
      /* presenting can throw when permission was revoked mid-flight */
    }
    void this.deps.ack(item.id).catch(() => {});
  }

  clear() {
    for (const id of [...this.armed.keys()]) this.cancel(id);
  }

  stop() {
    this.stopped = true;
    this.clear();
  }
}
